import { Injectable } from '@angular/core';
import { Cotizador } from './cotizadores.service';
import { AuthService } from './auth.service';
import { HttpHeaders, HttpParams, HttpClient } from '@angular/common/http';
import { BehaviorSubject, catchError, tap, throwError } from 'rxjs';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { AdminService } from './admin.service';
// Interfaces
export interface SubmenuItem {
  id?: string;
  description: string;
  route?: string;
  routeBlank?: string;
  info?: string;
  cotizador?: Cotizador;
  permissionExternalId?: string;
}

export interface MenuItem {
  id: string;
  description: string;
  iconRoute: string;
  route?: string;
  externalUrl?: string;
  subMenu?: SubmenuItem[];
  permissionExternalId?: string;
}

interface MenuResponse {
  data: {
    menu: {
      description: string;
      iconRoute: string;
      route?: string;
      subMenu?: {
        description: string;
        route?: string;
        routeBlank?: string;
        info?: string;
        cotizador?: Cotizador;
      }[];
    }[];
  };
  statusCode: number;
  messages: any[];
}

@Injectable({
  providedIn: 'root',
})
export class MenuDataService {
  private apiUrl = environment.int365_dom + '/api/v1';

  private menuItemsSubject = new BehaviorSubject<MenuItem[]>([]);
  public menuItems$ = this.menuItemsSubject.asObservable();

  constructor(
    private authService: AuthService,
    private http: HttpClient,
    private adminService: AdminService
  ) {
    const storedItems = sessionStorage.getItem('menuItems');
    if (storedItems) {
      const menuData = JSON.parse(storedItems);
      this.menuItemsSubject.next(menuData);
    }

    // Suscribirse a cambios en el rol de administrador
    this.adminService.adminRole$.subscribe((isAdmin) => {
      const menuItems = this.getMenuItems();
      // console.log(menuItems);
      this.menuItemsSubject.next(menuItems);
    });
  }

  /**
   * Consulta el menú por ClientId. Si envía ExternalId, filtra el menú del usuario.
   * @param ClientId Identificador del cliente
   * @param ExternalId Identificador externo del usuario (opcional; sin este param retorna todo el menú)
   */
  getMenuByUsers(
    ClientId: string,
    ExternalId?: string
  ): Observable<MenuResponse> {
    let params = new HttpParams().set('ClientId', ClientId);

    if (ExternalId) {
      params = params.set('ExternalId', ExternalId);
    }

    return this.http
      .get<MenuResponse>(`${this.apiUrl}/Menu`, {
        ...this.getHttpOptions(),
        params,
      })
      .pipe(
        tap((response) => {
          if (!response.data?.menu) {
            throw new Error('Formato de respuesta inválido');
          }
          return response;
        }),
        catchError((error) => {
          console.error('Error al obtener el menú:', error);
          return throwError(() => error);
        })
      );
  }

  getMenu(): Observable<any> {
    return this.http
      .get<any>(`${this.apiUrl}/Menu/FromToken`, this.getHttpOptions())
      .pipe(
        tap((response) => {
          const menuData: MenuItem[] = response.data.menu;
          sessionStorage.setItem('menuItems', JSON.stringify(menuData));
          this.menuItemsSubject.next(menuData);
        })
      );
  }

  getMenuItems(): MenuItem[] {
    const additionalMenuItems: MenuItem[] = [
      {
        id: 'menu_0_Principal',
        description: 'Principal',
        iconRoute: 'url(./assets/home.svg)',
        route: '/inicio',
      },
    ];

    // Obtener los elementos del menú actual
    const currentMenuItems = this.menuItemsSubject.getValue();

    // Verificar si los elementos adicionales ya están en el menú actual
    const existingIds = currentMenuItems.map((item) => item.id);
    const newMenuItems = additionalMenuItems.filter(
      (item) => !existingIds.includes(item.id)
    );

    return [...newMenuItems, ...currentMenuItems].map((item, index) => ({
      ...item,
      id: `menu_${index}_${item.description}`,
      subMenu: item.subMenu?.map((subItem, subIndex) => ({
        ...subItem,
        id: `submenu_${index}_${subIndex}_${subItem.description}`,
      })),
    }));
  }

  getInfoMenuItems(): MenuItem[] {
    return [
      {
        id: 'menu_1_Contactos',
        description: 'Contactos',
        iconRoute: 'url(./assets/contactos.svg)',
        route: '/inicio/contactos',
      },
      {
        id: 'menu_2_Documentos',
        description: 'Documentos',
        iconRoute: 'url(./assets/documentos.svg)',
        route: '/inicio/documentos',
      },
      ...(this.adminService.isAdmin()
        ? [
            {
              id: 'menu_3_Administrador',
              description: 'Administrador',
              iconRoute: 'url(./assets/administrador.svg)',
              route: '/inicio/admin',
            },
          ]
        : []),
      {
        id: 'menu_4_Buzon',
        description: 'Comunica tu experiencia',
        iconRoute: 'url(./assets/buzon.svg)',
        route: '/inicio/buzon',
      },
      {
        id: 'menu_5_preguntas_frecuentes',
        description: 'Preguntas frecuentes',
        iconRoute: 'url(./assets/question.svg)',
        externalUrl:
          environment.fe_portal_intermediarios_365_front_faq_document_url,
      },
    ];
  }

  // Autenticación
  private getHttpOptions(params?: any) {
    const token = this.authService.getToken();
    if (!token) throw new Error('No token found');
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });

    return {
      headers,
      params: params
        ? new HttpParams({ fromObject: this.formatParams(params) })
        : undefined,
    };
  }

  // Formato de parámetros
  private formatParams(params: any): { [key: string]: string } {
    const formattedParams: { [key: string]: string } = {};

    Object.entries(params).forEach(([key, value]) => {
      if (value === null || value === undefined) return;

      if (value instanceof Date) {
        formattedParams[key] = value.toISOString().split('T')[0];
      } else {
        formattedParams[key] = String(value);
      }
    });

    return formattedParams;
  }

  isRouteAllowed(route: string): boolean {
    const menuItems = this.menuItemsSubject.getValue();
    const fullRoute = `/inicio/${route}`;

    // Rutas siempre permitidas
    const allowedRoutes = [
      '/inicio',
      '/inicio/contactos',
      '/inicio/documentos',
      '/inicio/buzon',
      '/inicio/admin',
    ];

    if (allowedRoutes.includes(fullRoute) || allowedRoutes.includes(route)) {
      return true;
    }

    // Verificar si el usuario es administrador
    if (this.adminService.isAdmin() && route === 'admin') {
      return true;
    }

    // Verificar en el menú principal
    for (const item of menuItems) {
      if (item.route && `/inicio/${item.route}` === fullRoute) {
        return true;
      }

      // Verificar en submenús
      if (item.subMenu) {
        for (const subItem of item.subMenu) {
          if (subItem.route && `/inicio/${subItem.route}` === fullRoute) {
            return true;
          }
        }
      }
    }

    return false;
  }

  refreshMenu() {
    const menuItems = this.getMenuItems();
    this.menuItemsSubject.next(menuItems);
  }
}
